import React, { useState } from "react";
import { Trash2, Paperclip } from "lucide-react";
import { useQuestionOptions } from "./questionOptions";
import { validateInputValue, GuidanceBlock } from "./displayConditionUtils";
import { Country, State, City } from "country-state-city";

const CITY_MAP = {
  uttarakhand: [
    "Dehradun",
    "Haridwar",
    "Roorkee",
    "Nainital",
    "Rishikesh",
    "Haldwani",
    "Rudrapur",
    "Almora",
    "Pithoragarh",
    "Other",
  ],
  haryana: [
    "Gurugram / Gurgaon",
    "Faridabad",
    "Panipat",
    "Ambala",
    "Karnal",
    "Rohtak",
    "Hisar",
    "Sonipat",
    "Panchkula",
    "Yamunanagar",
    "Other",
  ],
  delhi: [
    "New Delhi",
    "Central Delhi",
    "North Delhi",
    "South Delhi",
    "East Delhi",
    "West Delhi",
    "Other",
  ],
  punjab: [
    "Ludhiana",
    "Amritsar",
    "Jalandhar",
    "Patiala",
    "Bathinda",
    "Mohali",
    "Pathankot",
    "Hoshiarpur",
    "Other",
  ],
  uttar_pradesh: [
    "Noida",
    "Greater Noida",
    "Lucknow",
    "Kanpur",
    "Agra",
    "Varanasi",
    "Prayagraj",
    "Ghaziabad",
    "Meerut",
    "Bareilly",
    "Aligarh",
    "Other",
  ],
  maharashtra: [
    "Mumbai",
    "Pune",
    "Nagpur",
    "Thane",
    "Nashik",
    "Aurangabad",
    "Navi Mumbai",
    "Solapur",
    "Kolhapur",
    "Other",
  ],
  gujarat: [
    "Ahmedabad",
    "Surat",
    "Vadodara",
    "Rajkot",
    "Bhavnagar",
    "Jamnagar",
    "Gandhinagar",
    "Anand",
    "Other",
  ],
  karnataka: [
    "Bengaluru / Bangalore",
    "Mysuru",
    "Hubballi",
    "Mangaluru",
    "Belagavi",
    "Davanagere",
    "Ballari",
    "Other",
  ],
  tamil_nadu: [
    "Chennai",
    "Coimbatore",
    "Madurai",
    "Tiruchirappalli",
    "Salem",
    "Tiruppur",
    "Erode",
    "Vellore",
    "Other",
  ],
  kerala: [
    "Thiruvananthapuram",
    "Kochi",
    "Kozhikode",
    "Thrissur",
    "Kollam",
    "Palakkad",
    "Alappuzha",
    "Kannur",
    "Other",
  ],
  rajasthan: [
    "Jaipur",
    "Jodhpur",
    "Kota",
    "Bikaner",
    "Ajmer",
    "Udaipur",
    "Bhilwara",
    "Alwar",
    "Sikar",
    "Other",
  ],
  west_bengal: [
    "Kolkata",
    "Howrah",
    "Durgapur",
    "Asansol",
    "Siliguri",
    "Other",
  ],
  telangana: ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Other"],
  madhya_pradesh: [
    "Indore",
    "Bhopal",
    "Jabalpur",
    "Gwalior",
    "Ujjain",
    "Other",
  ],
  bihar: ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Other"],
  jharkhand: ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Other"],
  assam: ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Other"],
  odisha: ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Other"],
  himachal_pradesh: [
    "Shimla",
    "Dharamshala",
    "Mandi",
    "Solan",
    "Kullu",
    "Other",
  ],
  jammu_kashmir: ["Srinagar", "Jammu", "Anantnag", "Other"],
  goa: ["Panaji", "Margao", "Vasco da Gama", "Other"],
};

const DEFAULT_CITIES = [
  "New Delhi",
  "Mumbai",
  "Bengaluru",
  "Kolkata",
  "Chennai",
  "Hyderabad",
  "Ahmedabad",
  "Pune",
  "Jaipur",
  "Lucknow",
  "Surat",
  "Kanpur",
  "Nagpur",
  "Indore",
  "Thane",
  "Bhopal",
  "Visakhapatnam",
  "Pimpri-Chinchwad",
  "Patna",
  "Vadodara",
  "Ghaziabad",
  "Ludhiana",
  "Agra",
  "Nashik",
  "Faridabad",
  "Gurugram",
  "Meerut",
  "Rajkot",
  "Varanasi",
  "Srinagar",
  "Aurangabad",
  "Dhanbad",
  "Amritsar",
  "Navi Mumbai",
  "Gwalior",
  "Jabalpur",
  "Coimbatore",
  "Vijayawada",
  "Jodhpur",
  "Madurai",
  "Raipur",
  "Kota",
  "Chandigarh",
  "Guwahati",
  "Dehradun",
  "Haridwar",
  "Other",
];

const DEFAULT_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Jammu & Kashmir",
  "Ladakh",
  "Chandigarh",
  "Puducherry",
  "Other",
];

const COUNTRY_STATE_MAP = {
  india: DEFAULT_STATES,
  united_states: [
    "California",
    "Texas",
    "Florida",
    "New York",
    "Illinois",
    "Pennsylvania",
    "Ohio",
    "Georgia",
    "North Carolina",
    "Michigan",
    "New Jersey",
    "Virginia",
    "Washington",
    "Arizona",
    "Massachusetts",
    "Tennessee",
    "Indiana",
    "Missouri",
    "Maryland",
    "Wisconsin",
    "Colorado",
    "Minnesota",
    "South Carolina",
    "Alabama",
    "Other",
  ],
  united_kingdom: [
    "England",
    "Scotland",
    "Wales",
    "Northern Ireland",
    "Greater London",
    "Other",
  ],
  uk_london: [
    "Greater London",
    "Central London",
    "Inner London",
    "Outer London",
    "Other",
  ],
  australia: [
    "New South Wales",
    "Victoria",
    "Queensland",
    "Western Australia",
    "South Australia",
    "Tasmania",
    "Australian Capital Territory",
    "Northern Territory",
    "Other",
  ],
  canada: [
    "Ontario",
    "Quebec",
    "British Columbia",
    "Alberta",
    "Manitoba",
    "Saskatchewan",
    "Nova Scotia",
    "New Brunswick",
    "Other",
  ],
  germany: [
    "Bavaria",
    "Baden-Württemberg",
    "North Rhine-Westphalia",
    "Hesse",
    "Saxony",
    "Berlin",
    "Hamburg",
    "Lower Saxony",
    "Other",
  ],
  france: [
    "Île-de-France",
    "Auvergne-Rhône-Alpes",
    "Nouvelle-Aquitaine",
    "Occitanie",
    "Hauts-de-France",
    "Provence-Alpes-Côte d'Azur",
    "Grand Est",
    "Other",
  ],
  finland: [
    "Uusimaa",
    "Pirkanmaa",
    "Southwest Finland",
    "North Ostrobothnia",
    "Central Finland",
    "Lapland",
    "Satakunta",
    "Other",
  ],
  china: [
    "Guangdong",
    "Shandong",
    "Henan",
    "Sichuan",
    "Jiangsu",
    "Zhejiang",
    "Hubei",
    "Hunan",
    "Shanghai",
    "Beijing",
    "Other",
  ],
  japan: [
    "Tokyo",
    "Kanagawa",
    "Osaka",
    "Aichi",
    "Saitama",
    "Chiba",
    "Hyogo",
    "Hokkaido",
    "Fukuoka",
    "Other",
  ],
  brazil: [
    "São Paulo",
    "Rio de Janeiro",
    "Minas Gerais",
    "Bahia",
    "Paraná",
    "Rio Grande do Sul",
    "Pernambuco",
    "Ceará",
    "Other",
  ],
  russia: [
    "Moscow",
    "Saint Petersburg",
    "Moscow Oblast",
    "Krasnodar Krai",
    "Tatarstan",
    "Sverdlovsk Oblast",
    "Other",
  ],
  russian_federation: [
    "Moscow",
    "Saint Petersburg",
    "Moscow Oblast",
    "Krasnodar Krai",
    "Tatarstan",
    "Sverdlovsk Oblast",
    "Other",
  ],
  italy: [
    "Lombardy",
    "Lazio",
    "Campania",
    "Veneto",
    "Sicily",
    "Emilia-Romagna",
    "Piedmont",
    "Tuscany",
    "Other",
  ],
  spain: [
    "Madrid",
    "Catalonia",
    "Andalusia",
    "Valencian Community",
    "Galicia",
    "Basque Country",
    "Other",
  ],
  netherlands: [
    "North Holland",
    "South Holland",
    "North Brabant",
    "Utrecht",
    "Gelderland",
    "Overijssel",
    "Other",
  ],
  switzerland: [
    "Zurich",
    "Berne",
    "Vaud",
    "Aargau",
    "St. Gallen",
    "Geneva",
    "Other",
  ],
  united_arab_emirates: [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Ras Al Khaimah",
    "Fujairah",
    "Umm Al Quwain",
    "Other",
  ],
  saudi_arabia: [
    "Riyadh",
    "Makkah",
    "Eastern Province",
    "Madinah",
    "Asir",
    "Other",
  ],
  singapore: [
    "Central Region",
    "East Region",
    "North Region",
    "North-East Region",
    "West Region",
    "Other",
  ],
  south_africa: [
    "Gauteng",
    "KwaZulu-Natal",
    "Western Cape",
    "Eastern Cape",
    "Limpopo",
    "Mpumalanga",
    "Other",
  ],
  mexico: [
    "Mexico City",
    "Jalisco",
    "Nuevo León",
    "State of Mexico",
    "Puebla",
    "Guanajuato",
    "Other",
  ],
  argentina: [
    "Buenos Aires",
    "Córdoba",
    "Santa Fe",
    "Mendoza",
    "Tucumán",
    "Other",
  ],
  austria: [
    "Vienna",
    "Lower Austria",
    "Upper Austria",
    "Styria",
    "Tyrol",
    "Other",
  ],
  belgium: ["Flanders", "Wallonia", "Brussels", "Other"],
  chile: ["Santiago Metropolitan", "Valparaíso", "Biobío", "Other"],
  colombia: ["Bogotá", "Antioquia", "Valle del Cauca", "Cundinamarca", "Other"],
  costa_rica: ["San José", "Alajuela", "Cartago", "Heredia", "Other"],
  czech_republic: [
    "Prague",
    "Central Bohemian",
    "South Moravian",
    "Moravian-Silesian",
    "Other",
  ],
  egypt: ["Cairo", "Giza", "Alexandria", "Dakahlia", "Other"],
  fiji: ["Central", "Western", "Northern", "Eastern", "Other"],
  greece: ["Attica", "Central Macedonia", "Crete", "Thessaly", "Other"],
  hong_kong_china: ["Hong Kong Island", "Kowloon", "New Territories", "Other"],
  indonesia: [
    "Jakarta",
    "West Java",
    "East Java",
    "Central Java",
    "Banten",
    "North Sumatra",
    "Other",
  ],
  ireland: ["Dublin", "Cork", "Galway", "Limerick", "Kildare", "Other"],
  israel: [
    "Center District",
    "Tel Aviv",
    "Haifa",
    "Northern District",
    "Southern District",
    "Jerusalem",
    "Other",
  ],
  jordan: ["Amman", "Irbid", "Zarqa", "Balqa", "Other"],
  kazakhstan: ["Almaty", "Astana", "Shymkent", "Karaganda", "Other"],
  korea: [
    "Seoul",
    "Gyeonggi-do",
    "Busan",
    "Incheon",
    "Gyeongsangnam-do",
    "Other",
  ],
  south_korea: [
    "Seoul",
    "Gyeonggi-do",
    "Busan",
    "Incheon",
    "Gyeongsangnam-do",
    "Other",
  ],
  macau_china: ["Macau Peninsula", "Taipa", "Coloane", "Other"],
  malaysia: [
    "Selangor",
    "Kuala Lumpur",
    "Johor",
    "Penang",
    "Sabah",
    "Sarawak",
    "Other",
  ],
  maldives: ["Malé", "Addu City", "Fuvahmulah", "Kulhudhuffushi", "Other"],
  new_zealand: [
    "Auckland",
    "Canterbury",
    "Wellington",
    "Waikato",
    "Bay of Plenty",
    "Other",
  ],
  oman: ["Muscat", "Dhofar", "Al Batinah North", "Dakhiliyah", "Other"],
  panama: ["Panama", "West Panama", "Chiriquí", "Colón", "Other"],
  peru: ["Lima", "Arequipa", "La Libertad", "Piura", "Cusco", "Other"],
  philippines: [
    "Metro Manila",
    "CALABARZON",
    "Central Luzon",
    "Central Visayas",
    "Western Visayas",
    "Other",
  ],
  poland: [
    "Masovian",
    "Silesian",
    "Greater Poland",
    "Lesser Poland",
    "Lower Silesian",
    "Other",
  ],
  portugal: ["Lisbon", "Porto", "Setúbal", "Braga", "Aveiro", "Other"],
  qatar: ["Doha", "Al Rayyan", "Al Wakrah", "Al Khor", "Other"],
  romania: ["Bucharest", "Cluj", "Timiș", "Iași", "Constanța", "Other"],
  thailand: [
    "Bangkok",
    "Nonthaburi",
    "Nakhon Ratchasima",
    "Chiang Mai",
    "Chonburi",
    "Other",
  ],
  turkey: ["Istanbul", "Ankara", "Izmir", "Bursa", "Antalya", "Other"],
  vietnam: [
    "Ho Chi Minh City",
    "Hanoi",
    "Da Nang",
    "Binh Duong",
    "Dong Nai",
    "Other",
  ],
  morocco: [
    "Casablanca-Settat",
    "Rabat-Salé-Kénitra",
    "Tanger-Tetouan-Al Hoceima",
    "Marrakesh-Safi",
    "Other",
  ],
  hungary: [
    "Budapest",
    "Pest",
    "Borsod-Abaúj-Zemplén",
    "Győr-Moson-Sopron",
    "Other",
  ],
};

const getStateListForCountry = (countryVal) => {
  if (!countryVal) return COUNTRY_STATE_MAP.india;
  const str = extractStateString(countryVal);

  const allCountries = Country.getAllCountries();
  const countryStr = str.toLowerCase();
  const foundCountry = allCountries.find(
    (c) =>
      c.name.toLowerCase() === countryStr ||
      c.isoCode.toLowerCase() === countryStr,
  );

  if (foundCountry) {
    const states = State.getStatesOfCountry(foundCountry.isoCode);
    if (states && states.length > 0) {
      return [...states.map((s) => s.name), "Other"];
    }
  }

  const cleanCountry = str.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!cleanCountry) return COUNTRY_STATE_MAP.india;

  for (const key of Object.keys(COUNTRY_STATE_MAP)) {
    const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (
      cleanCountry === cleanKey ||
      cleanCountry.includes(cleanKey) ||
      cleanKey.includes(cleanCountry)
    ) {
      return COUNTRY_STATE_MAP[key];
    }
  }

  return ["Other"];
};

const STATE_ALIASES = {
  up: "uttar_pradesh",
  uttarpradesh: "uttar_pradesh",
  uk: "uttarakhand",
  uttarakhand: "uttarakhand",
  uttaranchal: "uttarakhand",
  hr: "haryana",
  haryana: "haryana",
  dl: "delhi",
  delhi: "delhi",
  newdelhi: "delhi",
  pb: "punjab",
  punjab: "punjab",
  mh: "maharashtra",
  maharashtra: "maharashtra",
  gj: "gujarat",
  gujarat: "gujarat",
  ka: "karnataka",
  karnataka: "karnataka",
  tn: "tamil_nadu",
  tamilnadu: "tamil_nadu",
  kl: "kerala",
  kerala: "kerala",
  rj: "rajasthan",
  rajasthan: "rajasthan",
  wb: "west_bengal",
  westbengal: "west_bengal",
  ts: "telangana",
  telangana: "telangana",
  mp: "madhya_pradesh",
  madhyapradesh: "madhya_pradesh",
  br: "bihar",
  bihar: "bihar",
  jh: "jharkhand",
  jharkhand: "jharkhand",
  as: "assam",
  assam: "assam",
  od: "odisha",
  orissa: "odisha",
  odisha: "odisha",
  hp: "himachal_pradesh",
  himachalpradesh: "himachal_pradesh",
  jk: "jammu_kashmir",
  jammukashmir: "jammu_kashmir",
  ga: "goa",
  goa: "goa",
  cg: "chhattisgarh",
  chhattisgarh: "chhattisgarh",
  chattisgarh: "chhattisgarh",
};

const extractStateString = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object" && !Array.isArray(val)) {
    return val.value || val.label || val.name || "";
  }
  if (Array.isArray(val) && val.length > 0) {
    return extractStateString(val[0]);
  }
  return String(val);
};

const getCityListForState = (stateVal, countryVal) => {
  const isIndia =
    !countryVal || String(countryVal).toLowerCase().includes("india");
  const fallbackCities = ["Other"];

  if (!stateVal) return isIndia ? DEFAULT_CITIES : fallbackCities;
  const str = extractStateString(stateVal);

  // Attempt to match with country-state-city
  const allCountries = Country.getAllCountries();
  let targetCountryCode = "IN";
  if (countryVal) {
    const countryStr = extractStateString(countryVal).toLowerCase();
    const foundCountry = allCountries.find(
      (c) =>
        c.name.toLowerCase() === countryStr ||
        c.isoCode.toLowerCase() === countryStr,
    );
    if (foundCountry) {
      targetCountryCode = foundCountry.isoCode;
    }
  }

  const allStatesInCountry = State.getStatesOfCountry(targetCountryCode);
  const stateStrLower = str.toLowerCase();

  // Find matching state by exact name, fuzzy match, or isoCode
  const foundState = allStatesInCountry.find(
    (s) =>
      s.name.toLowerCase() === stateStrLower ||
      s.isoCode.toLowerCase() === stateStrLower ||
      s.name.toLowerCase().includes(stateStrLower) ||
      stateStrLower.includes(s.name.toLowerCase()),
  );

  if (foundState) {
    const cities = City.getCitiesOfState(targetCountryCode, foundState.isoCode);
    if (cities && cities.length > 0) {
      return [...cities.map((c) => c.name), "Other"];
    }
  }

  // Fallback to legacy Indian cities map if the above failed
  const cleanState = str.toLowerCase().replace(/[^a-z0-9]/g, "");

  if (STATE_ALIASES[cleanState]) {
    const canonicalKey = STATE_ALIASES[cleanState];
    if (CITY_MAP[canonicalKey]) {
      return CITY_MAP[canonicalKey];
    }
  }

  for (const key of Object.keys(CITY_MAP)) {
    const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (
      cleanState === cleanKey ||
      cleanState.includes(cleanKey) ||
      cleanKey.includes(cleanState)
    ) {
      return CITY_MAP[key];
    }
  }

  return isIndia ? DEFAULT_CITIES : fallbackCities;
};

const QuestionInput = ({
  isEmployee,
  field,
  value,
  onChange,
  onOptionsLoaded,
  disabled,
  rowData = {},
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { options, loading } = useQuestionOptions(
    field,
    onOptionsLoaded,
    isEmployee,
  );
  const type = field.questionType || field.fieldType;
  const isPercentage =
    type === "percentage" ||
    (field.questionText || field.fieldLabel || "")
      .toLowerCase()
      .includes("percentage");

  if (loading)
    return <span className="text-gray-400 text-sm">Loading options...</span>;

  let normalizedValue = Array.isArray(value)
    ? value.length > 0
      ? value[0]
      : ""
    : value;
  if (
    normalizedValue &&
    typeof normalizedValue === "object" &&
    !Array.isArray(normalizedValue)
  ) {
    normalizedValue =
      normalizedValue.value !== undefined
        ? normalizedValue.value
        : normalizedValue.answer !== undefined
          ? normalizedValue.answer
          : normalizedValue;
  }

  const normalizeComparable = (input) =>
    String(input ?? "")
      .trim()
      .toLowerCase();

  switch (type) {
    case "text": {
      const fieldId = String(
        field?.fieldId || field?.questionId || "",
      ).toLowerCase();
      const fieldLabel = String(
        field?.fieldLabel || field?.questionText || "",
      ).toLowerCase();
      const isCityField =
        fieldId === "city" ||
        fieldId.includes("city") ||
        fieldLabel.includes("city");
      const isStateField =
        fieldId === "state" ||
        fieldId.includes("state") ||
        fieldLabel.includes("state");

      let effectiveOptions = options;

      if (isCityField) {
        let selectedState =
          rowData?.india_state ||
          rowData?.australia_state ||
          rowData?.usa_state ||
          rowData?.state;
        if (!selectedState) {
          for (const [k, v] of Object.entries(rowData || {})) {
            if (k.toLowerCase().includes("state") && v) {
              selectedState = v;
              break;
            }
          }
        }

        let selectedCountry =
          rowData?.country || rowData?.corporate_headquarter_country;
        if (!selectedCountry) {
          for (const [k, v] of Object.entries(rowData || {})) {
            if (k.toLowerCase().includes("country") && v) {
              selectedCountry = v;
              break;
            }
          }
        }

        effectiveOptions = getCityListForState(selectedState, selectedCountry);
      } else if (isStateField) {
        let selectedCountry =
          rowData?.country || rowData?.corporate_headquarter_country;
        if (!selectedCountry && rowData && typeof rowData === "object") {
          for (const [k, v] of Object.entries(rowData || {})) {
            if (k.toLowerCase().includes("country") && v) {
              selectedCountry = v;
              break;
            }
          }
        }
        effectiveOptions = getStateListForCountry(selectedCountry);
      }

      if (effectiveOptions && effectiveOptions.length > 0) {
        const placeholder = isCityField
          ? "-- Select City --"
          : isStateField
            ? "-- Select State --"
            : "-- Select --";

        const stringVal = String(normalizedValue || "");
        const isKnownOption = effectiveOptions.some((opt) => {
          const val =
            typeof opt === "object" ? (opt?.value ?? opt?.label) : opt;
          return String(val).toLowerCase() === stringVal.toLowerCase();
        });

        const isOtherSelected =
          stringVal === "Other" ||
          stringVal === "other" ||
          stringVal.startsWith("Other:") ||
          (!isKnownOption && stringVal !== "");

        const customTextValue = stringVal.startsWith("Other:")
          ? stringVal.replace(/^Other:\s*/, "")
          : isKnownOption
            ? ""
            : stringVal === "Other" || stringVal === "other"
              ? ""
              : stringVal;

        return (
          <div className="space-y-2">
            <select
              value={isOtherSelected ? "Other" : normalizedValue || ""}
              disabled={disabled}
              onChange={(e) => {
                const selected = e.target.value;
                if (selected === "Other") {
                  onChange("Other");
                } else {
                  onChange(selected);
                }
              }}
              className={`w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white outline-none ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
            >
              <option value="">{placeholder}</option>
              {effectiveOptions.map((opt) => {
                const val =
                  typeof opt === "object" ? (opt?.value ?? opt?.label) : opt;
                const lbl =
                  typeof opt === "object" ? (opt?.label ?? opt?.value) : opt;
                return (
                  <option key={val} value={val}>
                    {lbl}
                  </option>
                );
              })}
            </select>

            {isOtherSelected && (
              <input
                type="text"
                value={customTextValue}
                disabled={disabled}
                onChange={(e) =>
                  onChange(
                    e.target.value ? `Other: ${e.target.value}` : "Other",
                  )
                }
                className={`w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none mt-2 ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
                placeholder={`Specify Other ${isCityField ? "City" : isStateField ? "State" : "Value"}`}
              />
            )}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={normalizedValue || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
          placeholder={`Enter ${field?.questionText || field?.fieldLabel || "value"}`}
        />
      );
    }

    case "date":
      return (
        <input
          type="date"
          value={normalizedValue || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
        />
      );

    case "number":
    case "percentage": {
      const rangeLimit =
        field.limit && field.limit.length > 0 ? field.limit[0] : null;

      const showPercentageSuffix =
        isPercentage &&
        !isFocused &&
        normalizedValue !== "" &&
        normalizedValue !== null &&
        normalizedValue !== undefined;
      const displayValue = showPercentageSuffix
        ? `${normalizedValue}%`
        : (normalizedValue ?? "");
      const inputType = showPercentageSuffix ? "text" : "number";

      return (
        <div className="relative w-full">
          <input
            type={inputType}
            min="0"
            value={displayValue}
            disabled={disabled}
            onChange={(e) => {
              onChange(validateInputValue(field, e.target.value));
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
            }}
            className={`w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${
              disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
            }`}
            placeholder={
              rangeLimit
                ? `Range ${rangeLimit}`
                : isPercentage
                  ? "0-100"
                  : "Enter value"
            }
          />
        </div>
      );
    }
    case "select":
      const candidateValues = Array.isArray(value)
        ? value.flatMap((item) => {
            if (item && typeof item === "object" && !Array.isArray(item)) {
              return [
                item.value !== undefined ? item.value : null,
                item.answer !== undefined ? item.answer : null,
                item.key !== undefined ? item.key : null,
              ].filter((v) => v !== null && v !== undefined && v !== "");
            }
            return item !== null && item !== undefined && item !== ""
              ? [item]
              : [];
          })
        : [normalizedValue].filter(
            (v) => v !== "" && v !== null && v !== undefined,
          );

      const selectedOpt = options.find((opt) => {
        const optValue = normalizeComparable(opt.value);
        const optLabel = normalizeComparable(opt.label);
        return candidateValues.some((candidate) => {
          const normalizedCandidate = normalizeComparable(candidate);
          return (
            normalizedCandidate === optValue || normalizedCandidate === optLabel
          );
        });
      });

      const finalSelectValue = selectedOpt
        ? selectedOpt.value
        : candidateValues[0] || "";

      return (
        <select
          value={finalSelectValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white outline-none"
        >
          <option value="">-- Select --</option>
          {options.map((opt) => (
            <option key={opt._id || opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case "multi-select":
    case "multiselect": {
      const current = Array.isArray(value) ? value : value ? [value] : [];
      if (
        field.fieldId === "item_type" ||
        field.questionId === "item_type" ||
        field.fieldId === "major_component_category"
      ) {
        console.log(
          `QuestionInput [${field.fieldId || field.questionId}] - Options:`,
          options.map((o) => o.value),
          "Current Value:",
          current,
        );
      }
      const hasHeaders = options.some((opt) => !opt.value || opt.value === "");

      if (!hasHeaders) {
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {options.map((opt) => (
              <label
                key={opt._id || opt.value}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
              >
                <input
                  type="checkbox"
                  checked={current.some(
                    (v) =>
                      String(v).trim().toLowerCase() ===
                      String(opt.value).trim().toLowerCase(),
                  )}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...current, opt.value]);
                    } else {
                      onChange(
                        current.filter((v) => String(v) !== String(opt.value)),
                      );
                    }
                  }}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
                <span className="text-gray-700 text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        );
      }

      const groupedOptions = [];
      let currentGroup = { header: null, options: [] };

      options.forEach((opt) => {
        if (!opt.value || opt.value === "") {
          if (currentGroup.options.length > 0) {
            groupedOptions.push(currentGroup);
          }
          currentGroup = { header: opt.label, options: [] };
        } else {
          currentGroup.options.push(opt);
        }
      });

      if (currentGroup.options.length > 0) {
        groupedOptions.push(currentGroup);
      }

      return (
        <div className="space-y-4">
          {groupedOptions.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-1">
              {group.header && (
                <div className="font-semibold text-gray-800 text-sm uppercase tracking-wide ">
                  {group.header}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-0">
                {group.options.map((opt) => (
                  <label
                    key={opt._id || opt.value}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={current.some(
                        (v) =>
                          String(v).trim().toLowerCase() ===
                          String(opt.value).trim().toLowerCase(),
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange([...current, opt.value]);
                        } else {
                          onChange(
                            current.filter(
                              (v) => String(v) !== String(opt.value),
                            ),
                          );
                        }
                      }}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500 flex-shrink-0"
                    />
                    <span className="text-gray-700 text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    case "file": {
      const files = Array.isArray(value) ? value : value ? [value] : [];
      return (
        <div className="space-y-3">
          <input
            type="file"
            multiple
            onChange={(e) => {
              const newFiles = Array.from(e.target.files || []);
              onChange([...files, ...newFiles]);
            }}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer"
          />
          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Uploaded Files:
              </p>
              <ul className="space-y-1">
                {files.map((file, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm hover:border-teal-300 transition-all duration-200 hover:shadow-md"
                  >
                    <div
                      className="flex items-center gap-2 overflow-hidden cursor-pointer group"
                      onClick={() => {
                        if (file instanceof File) {
                          const fileURL = URL.createObjectURL(file);
                          window.open(fileURL, "_blank");
                        } else if (typeof file === "string") {
                          let fileURL = file;
                          if (
                            !file.startsWith("http://") &&
                            !file.startsWith("https://")
                          ) {
                            const apiBase = process.env.REACT_APP_API_URL || "";
                            const cleanBase = apiBase.endsWith("/")
                              ? apiBase.slice(0, -1)
                              : apiBase;
                            const cleanPath = file.startsWith("/")
                              ? file
                              : `/${file}`;
                            fileURL = `${cleanBase}${cleanPath}`;
                          }
                          window.open(fileURL, "_blank");
                        }
                      }}
                    >
                      <div className="flex items-center justify-center p-1.5 bg-teal-50 rounded-md text-teal-600 group-hover:bg-teal-100 transition-colors">
                        <Paperclip size={14} />
                      </div>
                      <span className="text-[13px] font-medium text-gray-700 group-hover:text-teal-700 truncate transition-colors">
                        {file?.name ||
                          (typeof file === "string"
                            ? file.split("/").pop()
                            : "View File")}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        onChange(files.filter((_, i) => i !== idx))
                      }
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors ml-3 flex-shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
};

export default QuestionInput;
